import { css, html } from "../helpers/webComponents";
import { CanvasComponent } from "./CanvasComponent";

// CSS for the AudioSettingsComponent
const styles: string = css`
    :host {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        font-family: Arial, sans-serif;
    }

    .settings-container {
        background: #222;
        padding: 20px;
        border-radius: 10px;
        text-align: center;
    }

    input[type="range"] {
        width: 100%;
    }

    .button {
        background-color: #7f6ed7;
        border: 1px solid #332c57;
        padding: 5px 10px;
        margin: 10px 0;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .button:hover {
        background-color: #332c57;
    }
`;

export class AudioSettingsComponent extends HTMLElement {
    private _volume: number = 50;

    public connectedCallback(): void {
        console.log("AudioSettingsComponent connected!", this);

        this.attachShadow({ mode: "open" });

        // Load volume from localStorage
        const savedVolume: string | null = localStorage.getItem("audioVolume");
        if (savedVolume) {
            this._volume = parseInt(savedVolume, 10);
        }

        this.refreshAudioSettings();
        this.attachEventListeners();
    }

    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    // Refresh audio settings (fetch and render)
    private refreshAudioSettings(): void {
        // Possible to fetch or handle game-specific settings
        console.log("Refreshing audio settings...");

        // Render the component after refreshing
        this.render();
    }

    // Render the component UI
    private render(): void {
        if (!this.shadowRoot) return;

        const element: HTMLElement = this.renderAudioSettings();
        this.shadowRoot.innerHTML = "";
        this.shadowRoot.appendChild(element);
    }

    // Render the audio settings UI
    private renderAudioSettings(): HTMLElement {
        return html`
            <div>
                <style>
                    ${styles}
                </style>
                <div class="settings-container">
                    <h2>Audio Settings</h2>
                    <label for="volume">Volume: <span id="volumeValue">${this._volume}</span>%</label>
                    <input type="range" id="volume" min="0" max="100" value="${this._volume}">
                    <button id="close" class="button">Close</button>
                </div>
            </div>
        `;
    }

    // Attach event listeners to the elements inside shadow DOM
    private attachEventListeners(): void {
        const volumeInput: HTMLInputElement = this.shadowRoot?.querySelector("#volume") as HTMLInputElement;
        const closeButton: HTMLButtonElement = this.shadowRoot?.querySelector("#close") as HTMLButtonElement;

        // Update the volume when the slider is moved
        volumeInput.addEventListener("input", this.handleVolumeChange.bind(this));

        // Close the audio settings page when clicked
        closeButton.addEventListener("click", this.handleClose.bind(this));
    }

    // Remove event listeners when the component is disconnected
    private removeEventListeners(): void {
        const volumeInput: HTMLInputElement = this.shadowRoot?.querySelector("#volume") as HTMLInputElement;
        const closeButton: HTMLButtonElement = this.shadowRoot?.querySelector("#close") as HTMLButtonElement;

        // Remove event listeners for volume change and close button
        volumeInput.removeEventListener("input", this.handleVolumeChange.bind(this));
        closeButton.removeEventListener("click", this.handleClose.bind(this));
    }

    // Update global volume settings
    private updateVolume(): void {
        console.log(`Volume set to ${this._volume}%`);

        // Directly update the background music volume without unnecessary conditional check
        const rootComponent: CanvasComponent = document.querySelector("game-canvas") as CanvasComponent;
        rootComponent._backgroundMusic.volume = this._volume / 100;
    }

    // Handle volume change event
    private handleVolumeChange(event: Event): void {
        const target: HTMLInputElement = event.target as HTMLInputElement;

        this._volume = target.valueAsNumber;
        console.log("Volume changed to:", this._volume);

        // Update the volume label displayed on the UI
        const volumeLabel: HTMLSpanElement = this.shadowRoot?.querySelector("#volumeValue") as HTMLSpanElement;
        volumeLabel.textContent = `${this._volume}`;

        // Store the volume in localStorage
        localStorage.setItem("audioVolume", `${this._volume}`);

        // Update the input range value to reflect the updated volume
        const volumeInput: HTMLInputElement = this.shadowRoot?.querySelector("#volume") as HTMLInputElement;
        volumeInput.value = `${this._volume}`;

        // Dispatch the volume change event
        this.dispatchEvent(new CustomEvent("volume-changed", {
            detail: { volume: this._volume },
            bubbles: true,
            composed: true,
        }));

        this.updateVolume();
    }

    // Handle closing the audio settings page
    private handleClose(): void {
        this.remove();
    }
}
